import React, { useEffect, useState } from "react";
import { PageHeader, Card, Badge, Button, Spinner, Input, Textarea, Select } from "@/components/ui";
import { endpoints } from "@/lib/api";
import { Scale, AlertTriangle, FileText, Plus, Trash2 } from "lucide-react";

export default function Legal() {
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [running, setRunning] = useState(false);
  const [form, setForm] = useState({ title: "", doc_type: "NDA", party_a: "", party_b: "", content: "" });

  const load = async () => {
    const r = await endpoints.legal();
    setDocs(r.data);
    if (r.data.length && !selected) setSelected(r.data[0]);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const analyze = async (e) => {
    e.preventDefault();
    setRunning(true);
    try {
      const r = await endpoints.analyzeLegal(form);
      setSelected(r.data);
      setForm({ title: "", doc_type: "NDA", party_a: "", party_b: "", content: "" });
      await load();
    } finally { setRunning(false); }
  };

  const del = async (id) => {
    if (!confirm("Delete this document?")) return;
    await endpoints.deleteLegal(id);
    if (selected?.id === id) setSelected(null);
    load();
  };

  const riskTone = selected?.risk_level === "high" ? "red" : selected?.risk_level === "medium" ? "yellow" : "green";

  return (
    <div className="p-4 md:p-10 max-w-[1500px] mx-auto">
      <PageHeader
        eyebrow="CLARA Legal"
        title="Legal Intelligence"
        description="AI-powered contract review. Identifies red flags, exposures and dangerous clauses in seconds."
        testid="legal-page"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Input */}
        <Card>
          <div className="overline mb-4">Analyze Document</div>
          <form onSubmit={analyze} className="space-y-3">
            <div>
              <label className="text-[11px] text-white/50 mb-1 block">Title</label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="legal-title" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] text-white/50 mb-1 block">Type</label>
                <Select value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} data-testid="legal-type">
                  {["NDA", "SPA", "LOI", "Term Sheet", "Shareholders Agreement"].map((t) => <option key={t} value={t}>{t}</option>)}
                </Select>
              </div>
              <div>
                <label className="text-[11px] text-white/50 mb-1 block">Party A</label>
                <Input value={form.party_a} onChange={(e) => setForm({ ...form, party_a: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-[11px] text-white/50 mb-1 block">Party B</label>
              <Input value={form.party_b} onChange={(e) => setForm({ ...form, party_b: e.target.value })} />
            </div>
            <div>
              <label className="text-[11px] text-white/50 mb-1 block">Content</label>
              <Textarea required rows={8} placeholder="Paste the contract text..." value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="legal-content" />
            </div>
            <Button disabled={running} type="submit" className="w-full justify-center" data-testid="analyze-legal-btn">
              {running ? <><Spinner size={13} /> Analyzing…</> : <><Scale size={13} /> Analyze</>}
            </Button>
          </form>

          {docs.length > 0 && (
            <div className="mt-6 pt-6 border-t border-white/5">
              <div className="overline mb-3">History</div>
              <div className="space-y-1">
                {docs.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => setSelected(d)}
                    className={`w-full text-left px-2 py-1.5 rounded text-[12px] transition flex justify-between items-center ${selected?.id === d.id ? "bg-white/[0.04] text-white" : "text-white/50 hover:bg-white/[0.02]"}`}
                  >
                    <span className="truncate">{d.title}</span>
                    <Trash2 size={12} onClick={(e) => { e.stopPropagation(); del(d.id); }} className="text-white/30 hover:text-red-400 shrink-0 ml-2" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Analysis */}
        <div className="lg:col-span-2 space-y-5">
          {!selected ? (
            <Card>
              <div className="text-center py-16">
                <Scale size={32} className="mx-auto text-white/20 mb-3" />
                <div className="text-white/40">Submit a document to see red-flag analysis.</div>
              </div>
            </Card>
          ) : (
            <>
              <Card>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="overline mb-1">{selected.doc_type}</div>
                    <h2 className="font-display text-2xl">{selected.title}</h2>
                    <div className="text-[12px] text-white/40 mt-1">{selected.party_a} ↔ {selected.party_b}</div>
                  </div>
                  <Badge tone={riskTone}>{selected.risk_level} risk</Badge>
                </div>
                <div className="text-sm text-white/75 leading-relaxed">{selected.summary}</div>
              </Card>

              <Card>
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle size={16} className="text-gold" />
                  <div className="overline">Red Flags · {selected.red_flags?.length || 0}</div>
                </div>
                {selected.red_flags?.length ? (
                  <div className="space-y-3">
                    {selected.red_flags.map((f, i) => (
                      <div key={i} className="clara-surface-2 p-3 rounded-md border-l-2" style={{ borderLeftColor: f.severity === "high" ? "#ef4444" : f.severity === "medium" ? "#facc15" : "#34d399" }} data-testid={`red-flag-${i}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="text-[13px] font-medium">{f.clause}</div>
                          <Badge tone={f.severity === "high" ? "red" : f.severity === "medium" ? "yellow" : "green"}>{f.severity}</Badge>
                        </div>
                        <div className="text-[12px] text-white/60">{f.note}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-white/40 text-sm text-center py-8">No red flags detected.</div>
                )}
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
